import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { type StarFilter, useReviewUIStore } from "../store/use-review-ui-store";

const STAR_OPTIONS: StarFilter[] = [5, 4, 3, 2, 1];

interface ReviewFiltersProps {
  /**
   * Hides the "Replies" filter group. Only GOOGLE-source reviews are
   * repliable, so the in-app Platform page passes `showReplyFilter={false}`
   * — surfacing the filter there would always yield an empty result set.
   */
  showReplyFilter?: boolean;
}

export function ReviewFilters({ showReplyFilter = true }: ReviewFiltersProps = {}) {
  const { starFilter, replyFilter, setStarFilter, setReplyFilter } = useReviewUIStore();

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-muted-foreground uppercase tracking-wide mr-1">Stars</span>
        <FilterChip selected={starFilter === null} onClick={() => setStarFilter(null)}>
          All
        </FilterChip>
        {STAR_OPTIONS.map((value) => (
          <FilterChip
            key={value}
            selected={starFilter === value}
            onClick={() => setStarFilter(value)}
            aria-label={`${value} star${value === 1 ? "" : "s"}`}
          >
            {value}
            <Star className="ml-0.5 h-3 w-3 fill-amber-400 text-amber-400" />
          </FilterChip>
        ))}
      </div>

      {showReplyFilter && (
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground uppercase tracking-wide mr-1">
            Replies
          </span>
          <FilterChip selected={replyFilter === "all"} onClick={() => setReplyFilter("all")}>
            All
          </FilterChip>
          <FilterChip
            selected={replyFilter === "unreplied"}
            onClick={() => setReplyFilter("unreplied")}
          >
            Unreplied
          </FilterChip>
        </div>
      )}
    </div>
  );
}

interface FilterChipProps {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
  "aria-label"?: string;
}

function FilterChip({ selected, onClick, children, ...rest }: FilterChipProps) {
  return (
    <Button
      type="button"
      variant={selected ? "default" : "outline"}
      size="sm"
      onClick={onClick}
      className={cn("h-7 px-2.5 text-xs font-medium", selected && "shadow-sm")}
      {...rest}
    >
      {children}
    </Button>
  );
}
